import { Injectable } from '@angular/core';
import { ModelHelper } from '@dentsply-sirona/utilities';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { SortDirection } from 'src/app/modules/core/enums/sort-direction.enum';
import { CompanyType } from '../enums/company-types.enum';
import { UserState } from '../enums/user-state.enum';
import { UpdateServiceInterface } from '../interfaces/update-service.interface';
import { UserUpdateConfig } from '../interfaces/user-update-config.interface';
import { User } from '../models/user.model';
import { WhereFilter } from '../models/where-filter.model';
import { UserSchema } from '../schemas/user.schema';
import { ApiObservable } from '../types/api-observable.type';
import { GraphqlService } from './graphql.service';

/**
 * Service for communicating with the API in relation to user data
 */
@Injectable({
	providedIn: 'root'
})
export class UserService extends GraphqlService implements UpdateServiceInterface {
	/**
	 * Get user via uid
	 *
	 * @param userUid
	 * @return
	 */
	getUser(userUid: string): Observable<User> {
		return this.query(UserSchema.getUser, {
			uid: userUid
		}).pipe(map((data: any) => User.map(data.user)));
	}

	/**
	 * Get user via uid
	 *
	 * @param userUid
	 * @return
	 */
	getUserWithCredit(userUid: string): Observable<{ user: User }> {
		return this.query(UserSchema.getUserWithCredit, {
			uid: userUid
		}).pipe(
			map((data: { user: User }) => ({
				user: User.map(data.user)
			}))
		);
	}

	/**
	 * Benutzer-Liste laden
	 *
	 * @param filters Filter
	 * @param pageIndex Index der ersten Seite
	 * @param resultsPerPage Anzahl an Ergebnissen pro Seite
	 * @param orderBy Sortierung
	 * @param sortDirection Sortierreihenfolge
	 */
	getUserList(
		filters: WhereFilter[] = [],
		pageIndex: number = 0,
		resultsPerPage: number = 10,
		orderBy?: string,
		sortDirection?: string
	): Observable<{ totalCount: number; list: User[] }> {
		let isAscending = null;
		if (sortDirection === SortDirection.ASCENDING) {
			isAscending = 'true';
		} else if (sortDirection === SortDirection.DESCENDING) {
			isAscending = 'false';
		}
		return this.query(UserSchema.getUserList, {
			filters,
			pageIndex,
			resultsPerPage,
			orderBy,
			isAscending
		}).pipe(
			map((data: any) => ({
				totalCount: data.userlist.totalCount,
				list: ModelHelper.mapArray<User>(User, data.userlist.list)
			}))
		);
	}

	/**
	 * Create or update a user
	 * UpdateServiceInterface implementation
	 *
	 * @param uid
	 * @param data
	 * @param schema
	 * @param config
	 */
	update(uid: string, data: object, schema: any = UserSchema.updateUser, config?: UserUpdateConfig): ApiObservable<User> {
		const avatarGuid = config ? config.avatarGuid : null;

		if (!uid) {
			if (!data['roles']) {
				if (this.sessionService.currentUser.company.type === CompanyType.LAB) {
					data['roles'] = [{ name: 'LabUser' }];
				} else {
					data['roles'] = [{ name: 'DentistUser' }];
				}
			}
			if (!data['language']) {
				data['language'] = { code: 'en-US' };
			}
			if (!data['timezone']) {
				data['timezone'] = 'W. Europe Standard Time';
			}

			return this.createUser(data, avatarGuid);
		} else {
			return this.updateUser(
				uid,
				data,
				schema,
				avatarGuid,
				config ? config.deleteAvatar : false,
				config ? config.deleteSignatureFile : false
			);
		}
	}

	/**
	 * Create user
	 *
	 * @param data - User data
	 * @param avatarGuid Avatar-Guid
	 */
	createUser(data: object, avatarGuid?: string): ApiObservable<User> {
		return this.mutation(UserSchema.createUser, {
			user: data,
			avatarGuid
		}).pipe(map((response: any) => User.map(response.createUser)));
	}

	/**
	 * Update user
	 */
	updateUser(
		uid: string,
		data: object,
		schema: any = UserSchema.updateUser,
		avatarGuid: string = null,
		deleteAvatar: boolean = false,
		deleteSignatureFile: boolean = false
	): ApiObservable<User> {
		return this.mutation(schema, {
			uid,
			user: data,
			avatarGuid,
			deleteAvatar,
			deleteSignatureFile
		}).pipe(map((response: any) => User.map(response.updateUser)));
	}

	/**
	 * Change password
	 *
	 * @param oldPassword Old password
	 * @param newPassword New password
	 */
	changePassword(oldPassword: string, newPassword: string): ApiObservable {
		return this.mutation(UserSchema.changeUserPassword, {
			oldPassword,
			newPassword
		}).pipe(
			map((response: any) => ({
				code: response.changeUserPassword,
				data: null
			}))
		);
	}

	/**
	 * Set password after retrieving an reset password mail with valid hash
	 *
	 * @param hash
	 * @param newPassword
	 * @param hasAcceptedTermsAndConditions
	 * @param showSuccessMessage indicates if a success message box is shown to the user
	 */
	setPassword(
		hash: string,
		newPassword: string,
		hasAcceptedTermsAndConditions: boolean = false,
		showSuccessMessage: boolean = true
	): Observable<boolean> {
		return this.mutation(
			UserSchema.setUserPassword,
			{
				hash,
				newPassword,
				hasAcceptedTermsAndConditions
			},
			{
				showSuccessMessage
			}
		).pipe(map((response: any) => response.resetPassword));
	}

	/**
	 * Update user state
	 *
	 * @param uid UID of user
	 * @param userState State of the user
	 */
	updateUserState(uid: string, userState: UserState): Observable<User> {
		return this.mutation(UserSchema.updateUserState, {
			uid,
			state: userState
		}).pipe(map((response: any) => User.map(response.updateUserState)));
	}

	/**
	 * Check if username is unique
	 *
	 * @param username
	 */
	isUsernameUnique(username: string): Observable<boolean> {
		return this.query(UserSchema.isUsernameUnique, {
			username
		}).pipe(map((data: any) => data.isUsernameUnique));
	}

	/**
	 * Check if user is last company admin
	 *
	 * @param userUid
	 */
	isLastCompanyAdmin(userUid: string): Observable<boolean> {
		return this.query(UserSchema.isLastCompanyAdmin, {
			userUid
		}).pipe(map((data: any) => data.isLastCompanyAdmin));
	}

	/**
	 * Create new account
	 *
	 * @param isDentist - true for dentist, false for lab
	 * @param userData - The new user data
	 * @param companyData - The new company data
	 */
	createAccount(isDentist: boolean, userData: any, companyData: any): ApiObservable<User> {
		return this.mutation(
			UserSchema.createAccount,
			{
				isDentist,
				user: userData,
				company: companyData
			},
			{ showSuccessMessage: false }
		).pipe(map((response: any) => User.map(response.createAccount)));
	}

	/**
	 * Activate new account
	 *
	 * @param hash
	 */
	activateAccount(
		hash: string
	): ApiObservable<{
		activationWasSuccessful: boolean;
		companyName: string;
		errorCode: string;
	}> {
		return this.mutation(
			UserSchema.activateAccount,
			{
				hash
			},
			{ showSuccessMessage: false }
		).pipe(map((response: any) => response.activateAccount));
	}

	/**
	 * Get the users password expiration date by hash
	 *
	 * @param hash
	 * @return
	 */
	getUserPasswordExpirationDate(hash: string) {
		return this.query(UserSchema.getUserPasswordExpirationDate, {
			hash
		});
	}
}
